// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * Resolución centralizada del ID de contacto.
 * Evita inconsistencias entre ContactList, ContactEditModal y ConversationView.
 * @file contactIdResolver.js
 */

/**
 * Obtiene el identificador único del contacto con prioridad estándar.
 * @param {Object} contact - Objeto contacto (puede ser contactData, selectedContact, conversation.contact, etc.)
 * @returns {number|string|null} ID del contacto o null si no hay ninguno válido
 */
export function resolveContactId(contact) {
  if (contact == null || typeof contact !== 'object') {
    return null;
  }
  return (
    contact.usuario_contacto_id ??
    contact.contacto_id ??
    contact.otro_usuario_id ??
    contact.usuario_id ??
    contact.id ??
    null
  );
}

/**
 * @param {unknown} raw
 * @param {number|string|null|undefined} viewerUsuarioId - Usuario logueado; si el candidato es el mismo, no es peer.
 * @returns {boolean}
 */
function isDistinctPeerCandidate(raw, viewerUsuarioId) {
  if (raw == null || raw === '') return false;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (!Number.isFinite(n) || n <= 0) return false;
  if (viewerUsuarioId != null && viewerUsuarioId !== '') {
    const v = Number(viewerUsuarioId);
    if (Number.isFinite(v) && v > 0 && n === v) return false;
  }
  return true;
}

/**
 * ID del **usuario** (tabla usuarios) con quien se relaciona el contacto/conversación.
 * Obligatorio para GET `/api/presence/status.php?usuario_id=` — no usar `usuario_contacto_id` como ID de fila genérico sin contexto.
 *
 * **Nunca** usar aquí `contact.usuario_id` como respaldo: en `ContactFormatter::formatContactFromTable`
 * ese campo es el **dueño de la lista** (usuario logueado), no el peer. Incluirlo hace que todos los
 * contactos resuelvan al mismo ID → `statusByUser` y caché muestran el estado del usuario actual en todas las filas.
 *
 * **Nunca** aceptar `contact.user` si su id coincide con `viewerUsuarioId`: en payloads de conversación
 * `user` suele ser el **viewer**; usarlo como peer contaminaba todas las filas con la presencia del usuario actual.
 * `participant` se evalúa antes que `user` para priorizar al otro participante.
 *
 * @param {object|null|undefined} contact
 * @param {number|string|null|undefined} [viewerUsuarioId] - ID del usuario logueado (AuthContext)
 * @returns {number|null}
 */
export function resolvePeerUsuarioIdForPresence(contact, viewerUsuarioId = null) {
  if (contact == null || typeof contact !== 'object') {
    return null;
  }
  const candidates = [
    contact.otro_usuario_id,
    contact.usuario_destino_id,
    contact.destino_usuario_id,
    contact.other_user_id,
    contact.peer_usuario_id,
    contact.contacto_id,
    contact.usuario_contacto_id,
  ];
  for (const raw of candidates) {
    if (!isDistinctPeerCandidate(raw, viewerUsuarioId)) continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const p = contact.participant;
  if (p && typeof p === 'object') {
    const pid = p.id ?? p.usuario_id;
    if (isDistinctPeerCandidate(pid, viewerUsuarioId)) {
      const n = Number(pid);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  const u = contact.user;
  if (u && typeof u === 'object') {
    const uid = u.id ?? u.usuario_id;
    if (isDistinctPeerCandidate(uid, viewerUsuarioId)) {
      const n = Number(uid);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}
